import React, { useEffect, useState } from "react";
import Layout from "../../Components/Layout";
import { useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import toast from "react-hot-toast";

const WOstatusUpdate = () => {
  const location = useLocation();
  const navigate = useNavigate();

  const orderId = location.state?.orderId || null;

  const [existingReceipt, setExistingReceipt] = useState(null);
  const [receiptLoading, setReceiptLoading] = useState(false);

  const [workOrder, setWorkOrder] = useState({
    receiptId: null,
    orderId: null,

    name: "",
    organizationName: "",
    email: "",
    mobileNumber: "",

    address: {
      streetLine1: "",
      streetLine2: "",
      city: "",
      state: "",
      postalCode: "",
      country: "",
    },

    orderType: "",
    orderDate: "",
    referenceOrderNumber: "",
    orderFor: "",

    // Original order items
    orderItems: [],

    // Receipt rows
    receiptItems: [],
  });

  // ============================================================
  // GET ORDER RECEIPT BY ORDER ID
  // ============================================================

  const fetchOrderReceipt = async () => {
    if (!orderId) {
      toast.error("Work Order ID is missing");
      return;
    }

    try {
      setReceiptLoading(true);

      const token = localStorage.getItem("token");

      const response = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/order-receipts/${orderId}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );

      const data = response.data;

      console.log("ORDER RECEIPT RESPONSE:", data);

      setExistingReceipt(data);

      // ----------------------------------------------------------
      // Map orderItems
      // ----------------------------------------------------------

      const orderItems = Array.isArray(data.orderItems)
        ? data.orderItems.map((item) => ({
            itemId: item.itemId,
            description: item.description || "",
            quantity: Number(item.quantity || 0),
            unitPrice: Number(item.unitPrice || 0),
            discount: Number(item.discount || 0),
            sac: item.sac || null,
          }))
        : [];

      // ----------------------------------------------------------
      // Map already received items
      // ----------------------------------------------------------

      const existingReceiptItems = Array.isArray(data.receiptItems)
        ? data.receiptItems
        : Array.isArray(data.items)
          ? data.items
          : [];

      const receiptItems = existingReceiptItems.map((item, index) => {
        const matchedOrderItem = Array.isArray(data.orderItems)
          ? data.orderItems.find(
              (orderItem) =>
                Number(orderItem.itemId) ===
                Number(item.orderItemId ?? item.itemId),
            )
          : null;

        const itemQuantity = Number(
          item.quantity ?? matchedOrderItem?.quantity ?? 0,
        );

        return {
          receiptRowId: `existing-${index}`,

          orderItemId: Number(
            item.orderItemId ?? item.itemId ?? matchedOrderItem?.itemId ?? 0,
          ),

          description: item.description || matchedOrderItem?.description || "",

          quantity: itemQuantity,

          receivedQuantity: Number(item.receivedQuantity || 0),

          remainingQuantity: Number(
            item.remainingQuantity ??
              Math.max(itemQuantity - Number(item.receivedQuantity || 0), 0),
          ),

          receivedDate: item.receivedDate
            ? item.receivedDate.substring(0, 10)
            : "",

          status: item.status || "RECEIVED",

          isExisting: true,
        };
      });

      // ----------------------------------------------------------
      // Set all fields from API
      // ----------------------------------------------------------

      setWorkOrder({
        receiptId: data.receiptId || null,

        orderId: data.orderId || Number(orderId),

        name: data.name || "",

        organizationName: data.organizationName || "",

        email: data.email || "",

        mobileNumber: data.mobileNumber || "",

        address: {
          streetLine1: data.address?.streetLine1 || "",

          streetLine2: data.address?.streetLine2 || "",

          city: data.address?.city || "",

          state: data.address?.state || "",

          postalCode: data.address?.postalCode || "",

          country: data.address?.country || "",
        },

        orderType: data.orderType || "",

        orderDate: data.orderDate || "",

        referenceOrderNumber: data.referenceOrderNumber || "",

        orderFor: data.orderFor || "",

        orderItems,

        receiptItems,
      });
    } catch (error) {
      console.error("GET ORDER RECEIPT ERROR:", error);

      if (error.response?.status === 404) {
        /*
         * If there is no receipt yet, we still need
         * the order information.
         *
         * In that case call the normal order API.
         */
        await fetchOrderFallback();

        setExistingReceipt(null);

        return;
      }

      toast.error(
        error.response?.data?.message || "Failed to fetch Work Order",
      );
    } finally {
      setReceiptLoading(false);
    }
  };

  // ============================================================
  // FALLBACK - IF RECEIPT DOES NOT EXIST
  // ============================================================

  const fetchOrderFallback = async () => {
    try {
      const token = localStorage.getItem("token");

      const response = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/orders/${orderId}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );

      const data = response.data;

      const orderItems = Array.isArray(data.items)
        ? data.items.map((item) => ({
            itemId: item.itemId,
            description: item.description || "",
            quantity: Number(item.quantity || 0),
            unitPrice: Number(item.unitPrice || 0),
            discount: Number(item.discount || 0),
          }))
        : [];

      setWorkOrder({
        receiptId: null,

        orderId: Number(orderId),

        name: data.firstName || "",

        organizationName: data.organizationName || "",

        email: data.email || "",

        mobileNumber: data.mobileNumber || "",

        address: {
          streetLine1: data.address?.streetLine1 || "",

          streetLine2: data.address?.streetLine2 || "",

          city: data.address?.city || "",

          state: data.address?.state || "",

          postalCode: data.address?.postalCode || "",

          country: data.address?.country || "",
        },

        orderType: data.orderType || "",

        orderDate: data.orderDate || "",

        referenceOrderNumber: data.referenceOrderNumber || "",

        orderFor: data.orderFor || "",

        orderItems,

        receiptItems: [],
      });
    } catch (error) {
      console.error("GET ORDER FALLBACK ERROR:", error);

      toast.error("Failed to fetch Work Order");
    }
  };

  // ============================================================
  // INITIAL LOAD
  // ============================================================

  useEffect(() => {
    fetchOrderReceipt();
  }, [orderId]);

  // ============================================================
  // ADD NEW RECEIPT ROW
  // ============================================================

  const addMoreRow = () => {
    if (!workOrder.orderItems.length) {
      toast.error("No order item found");
      return;
    }

    const newRows = workOrder.orderItems
      .map((orderItem) => {
        const orderItemId = Number(orderItem.itemId ?? 0);

        const alreadyReceived = workOrder.receiptItems.reduce((sum, row) => {
          if (Number(row.orderItemId ?? row.itemId) !== orderItemId) {
            return sum;
          }

          return sum + Number(row.receivedQuantity || 0);
        }, 0);

        const remaining = Math.max(
          Number(orderItem.quantity || 0) - alreadyReceived,
          0,
        );

        if (remaining <= 0) {
          return null;
        }

        return {
          receiptRowId: `new-${Date.now()}-${orderItemId}`,

          orderItemId,

          itemId: orderItemId,

          description: orderItem.description || "",

          quantity: Number(orderItem.quantity || 0),

          receivedQuantity: "",

          remainingQuantity: '',

          receivedDate: new Date().toISOString().split("T")[0],

          status: "PENDING",

          isExisting: false,
        };
      })
      .filter(Boolean);

    if (!newRows.length) {
      toast.error("Full quantity has already been received for all items");
      return;
    }

    setWorkOrder((prev) => ({
      ...prev,

      receiptItems: [...prev.receiptItems, ...newRows],
    }));
  };

  // ============================================================
  // RECEIVED QUANTITY CHANGE
  // ============================================================

  const handleReceivedQuantityChange = (index, value) => {
    setWorkOrder((prev) => {
      const updatedRows = [...prev.receiptItems];

      const currentRow = updatedRows[index];

      if (!currentRow) {
        return prev;
      }

      const orderItemId = Number(
        currentRow.orderItemId ?? currentRow.itemId ?? 0,
      );

      const matchingOrderItem = prev.orderItems.find(
        (item) => Number(item.itemId) === orderItemId,
      );

      const orderQuantity = Number(
        currentRow.quantity || matchingOrderItem?.quantity || 0,
      );

      const receivedFromOtherRows = updatedRows.reduce((sum, row, rowIndex) => {
        if (rowIndex === index) {
          return sum;
        }

        if (Number(row.orderItemId ?? row.itemId) !== orderItemId) {
          return sum;
        }

        return sum + Number(row.receivedQuantity || 0);
      }, 0);

      const maxAllowed = Math.max(orderQuantity - receivedFromOtherRows, 0);

      let receivedQuantity = Number(value || 0);

      if (receivedQuantity < 0) {
        receivedQuantity = 0;
      }

      if (receivedQuantity > maxAllowed) {
        receivedQuantity = maxAllowed;
      }

      const remainingQuantity = Math.max(orderQuantity - receivedQuantity, 0);

      let status = "PENDING";

      if (receivedQuantity >= orderQuantity) {
        status = "COMPLETED";
      } else if (receivedQuantity > 0) {
        status = "RECEIVED";
      }

      updatedRows[index] = {
        ...currentRow,

        receivedQuantity,

        // remainingQuantity,

        status,
      };

      return {
        ...prev,

        receiptItems: updatedRows,
      };
    });
  };

  // ============================================================
  // RECEIVED DATE CHANGE
  // ============================================================

  const handleReceivedDateChange = (index, value) => {
    setWorkOrder((prev) => {
      const updatedRows = [...prev.receiptItems];

      if (!updatedRows[index]) {
        return prev;
      }

      updatedRows[index] = {
        ...updatedRows[index],

        receivedDate: value,
      };

      return {
        ...prev,

        receiptItems: updatedRows,
      };
    });
  };

  // ============================================================
  // REMOVE ROW
  // ============================================================

  const removeRow = (index) => {
    setWorkOrder((prev) => ({
      ...prev,

      receiptItems: prev.receiptItems.filter((_, i) => i !== index),
    }));
  };

  // ============================================================
  // POST / PATCH
  // ============================================================

  const handleUpdateWOReceipt = async () => {
    try {
      if (!orderId) {
        toast.error("Work Order ID is missing");
        return;
      }

      const token = localStorage.getItem("token");

      if (!token) {
        toast.error("Authentication token not found");
        return;
      }

      if (workOrder.receiptItems.length === 0) {
        toast.error("Please click Add More and enter received quantity");
        return;
      }

      /*
       * Validate rows
       */
      for (let i = 0; i < workOrder.receiptItems.length; i++) {
        const row = workOrder.receiptItems[i];

        const receivedQuantity = Number(row.receivedQuantity || 0);

        if (receivedQuantity <= 0) {
          toast.error(`Please enter received quantity for row ${i + 1}`);
          return;
        }

        if (!row.receivedDate) {
          toast.error(`Please select received date for row ${i + 1}`);
          return;
        }
      }

      /*
       * Prepare API items.
       */
      const receiptItems = workOrder.receiptItems.map((row) => {
        const receivedQuantity = Number(row.receivedQuantity || 0);
        const orderItemId = Number(row.orderItemId ?? row.itemId ?? 0);

        const matchingOrderItem = workOrder.orderItems.find(
          (item) => Number(item.itemId) === orderItemId,
        );

        const orderQuantity = Number(
          matchingOrderItem?.quantity ?? row.quantity ?? 0,
        );

        const remainingQuantity = Math.max(orderQuantity - receivedQuantity, 0);

        return {
          orderItemId,

          receivedDate: new Date(row.receivedDate).toISOString(),

          receivedQuantity,

          remainingQuantity,

          description: row.description || "",

          quantity: Number(row.quantity || 0),

          status: receivedQuantity > 0 ? "RECEIVED" : "PENDING",
        };
      });

      /*
       * --------------------------------------------------
       * IMPORTANT:
       *
       * Decide POST/PATCH from whether API returned
       * existing receipt items.
       * --------------------------------------------------
       */
      const hasExistingReceipt =
        (Array.isArray(existingReceipt?.receiptItems) &&
          existingReceipt.receiptItems.length > 0) ||
        (Array.isArray(existingReceipt?.items) &&
          existingReceipt.items.length > 0);

      const newReceiptItems = hasExistingReceipt
        ? receiptItems.filter(
            (_, index) => !workOrder.receiptItems[index]?.isExisting,
          )
        : receiptItems;

      if (hasExistingReceipt && newReceiptItems.length === 0) {
        toast.error("No new receipt item to update");
        return;
      }

      /*
       * --------------------------------------------------
       * FIRST RECEIPT
       * --------------------------------------------------
       */

      if (!hasExistingReceipt) {
        const payload = {
          orderId: Number(orderId),

          items: receiptItems,
        };

        console.log("POST /order-receipts", payload);

        await axios.post(
          `${process.env.REACT_APP_BACKEND_URL}/order-receipts`,
          payload,
          {
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
            },
          },
        );

        toast.success("WO Received Data Added Successfully");
      } else {
        /*
         * --------------------------------------------------
         * EXISTING RECEIPT: only send rows newly added
         * --------------------------------------------------
         */
        const payload = {
          items: newReceiptItems,
        };

        console.log(`PATCH /order-receipts/${orderId}`, payload);

        await axios.patch(
          `${process.env.REACT_APP_BACKEND_URL}/order-receipts/${orderId}`,
          payload,
          {
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
            },
          },
        );

        toast.success("WO Received Data Updated Successfully");
      }

      navigate("/work-orderStatus");
    } catch (error) {
      console.error("Receipt Update Error:", error);

      console.error("Backend response:", error.response?.data);

      toast.error(
        error.response?.data?.message ||
          error.response?.data?.error ||
          "Failed to update WO received data",
      );
    }
  };

  // ============================================================
  // ADDRESS
  // ============================================================

  const address = [
    workOrder.address.streetLine1,
    workOrder.address.streetLine2,
    workOrder.address.city,
    workOrder.address.state,
    workOrder.address.postalCode,
    workOrder.address.country,
  ]
    .filter(Boolean)
    .join(", ");

  // ============================================================
  // UI
  // ============================================================

  return (
    <Layout>
      <div className="backgound-main-content py-4">
        <div className="container-fluid">
          <button className="btn btn-primary mb-3" onClick={() => navigate(-1)}>
            Back
          </button>

          <div
            className="bg-white border p-3"
            style={{
              maxWidth: "1100px",
              margin: "0 auto",
            }}
          >
            {/* ================================================= */}
            {/* WORK ORDER INFORMATION */}
            {/* ================================================= */}

            <h4 className="fw-bold border-bottom pb-2">Work Order Details</h4>

            <div className="row">
              <div className="col-md-6 mb-3">
                <label className="fw-bold">Name</label>

                <input
                  className="form-control"
                  value={workOrder.name}
                  readOnly
                />
              </div>

              <div className="col-md-6 mb-3">
                <label className="fw-bold">Organization</label>

                <input
                  className="form-control"
                  value={workOrder.organizationName}
                  readOnly
                />
              </div>

              <div className="col-md-6 mb-3">
                <label className="fw-bold">Email</label>

                <input
                  className="form-control"
                  value={workOrder.email}
                  readOnly
                />
              </div>

              <div className="col-md-6 mb-3">
                <label className="fw-bold">Mobile Number</label>

                <input
                  className="form-control"
                  value={workOrder.mobileNumber}
                  readOnly
                />
              </div>

              <div className="col-md-6 mb-3">
                <label className="fw-bold">Reference Order Number</label>

                <input
                  className="form-control"
                  value={workOrder.referenceOrderNumber}
                  readOnly
                />
              </div>

              <div className="col-md-6 mb-3">
                <label className="fw-bold">Order Date</label>

                <input
                  type="date"
                  className="form-control"
                  value={workOrder.orderDate}
                  readOnly
                />
              </div>

              <div className="col-md-12 mb-3">
                <label className="fw-bold">Address</label>

                <textarea
                  rows="3"
                  className="form-control"
                  value={address}
                  readOnly
                />
              </div>
            </div>

            {/* ================================================= */}
            {/* RECEIPT TABLE */}
            {/* ================================================= */}

            <h4 className="fw-bold mt-4">Received Items</h4>

            <div className="table-responsive mt-3">
              <table className="table table-bordered align-middle">
                <thead className="table-light text-center">
                  <tr>
                    <th>Item #</th>

                    <th>Description</th>

                    <th>Total Quantity</th>

                    <th>Received Quantity</th>

                    <th>Remaining Quantity</th>

                    <th>Received Date</th>

                    <th>Action</th>
                  </tr>
                </thead>

                <tbody>
                  {workOrder.receiptItems.length === 0 ? (
                    <tr>
                      <td colSpan="7" className="text-center text-muted py-4">
                        No received items yet.
                        <br />
                        Click <strong>Add More</strong> to add a receipt.
                      </td>
                    </tr>
                  ) : (
                    workOrder.receiptItems.map((item, index) => {
                      const quantity = Number(item.quantity || 0);

                      // const remaining = Number(
                      //   item.remainingQuantity ??
                      //     Math.max(
                      //       quantity - Number(item.receivedQuantity || 0),
                      //       0,
                      //     ),
                      // );

                      return (
                        <tr key={item.receiptRowId}>
                          <td className="text-center fw-bold">{index + 1}</td>

                          {/* DESCRIPTION - READ ONLY */}
                          <td>
                            <textarea
                              rows="2"
                              className="form-control"
                              value={item.description}
                              readOnly
                            />
                          </td>

                          {/* TOTAL QUANTITY - READ ONLY */}
                          <td>
                            <input
                              type="number"
                              className="form-control text-center"
                              value={quantity}
                              readOnly
                            />
                          </td>

                          {/* RECEIVED QUANTITY - EDITABLE */}
                          <td>
                            <input
                              type="number"
                              min="0"
                              className="form-control"
                              value={item.receivedQuantity ?? ""}
                              onChange={(e) =>
                                handleReceivedQuantityChange(
                                  index,
                                  e.target.value,
                                )
                              }
                            />
                          </td>

                          {/* REMAINING - READ ONLY */}
                          <td>
                            <input
                              type="number"
                              className="form-control text-center fw-bold"
                              value={item.remainingQuantity ?? ""}
                              readOnly
                            />
                          </td>

                          {/* DATE - EDITABLE */}
                          <td>
                            <input
                              type="date"
                              className="form-control"
                              value={item.receivedDate || ""}
                              onChange={(e) =>
                                handleReceivedDateChange(index, e.target.value)
                              }
                            />
                          </td>

                          <td className="text-center">
                            <button
                              type="button"
                              className="btn btn-danger btn-sm"
                              onClick={() => removeRow(index)}
                              disabled={Boolean(item.isExisting)}
                            >
                              Remove
                            </button>
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>

            {/* ================================================= */}
            {/* ADD MORE */}
            {/* ================================================= */}

            <button
              type="button"
              className="btn btn-primary mt-2"
              onClick={addMoreRow}
              disabled={receiptLoading || workOrder.orderItems.length === 0}
            >
              Add More
            </button>

            {/* ================================================= */}
            {/* UPDATE */}
            {/* ================================================= */}

            <div className="mt-4">
              <button
                type="button"
                className="btn btn-success"
                onClick={handleUpdateWOReceipt}
                disabled={receiptLoading}
              >
                Update WO Received Data
              </button>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default WOstatusUpdate;
